import { useEffect, useState } from "react";
import { CalendarClock, MapPin, Users } from "lucide-react";

/** Wave 4 item 3: pre-meeting context capsule.
 *
 *  When a calendar event starts within the next 60 minutes, surface
 *  a "Coming up" strip on Today with a 2-sentence pre-meeting brief
 *  the server writes with local Ollama (qwen3:8b) from the attendee
 *  list + recent conversation context. Self-hides when nothing is
 *  imminent so it never adds empty chrome. */
interface Ev {
  event_id: string;
  summary: string;
  start_at: string;
  location: string | null;
  attendees: string[];
  brief: string;
}
interface Resp {
  ok: boolean;
  next: Ev | null;
  more: number;
  events: Ev[];
}

export function ComingUpCapsule() {
  const [data, setData] = useState<Resp | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = () =>
      fetch("/api/today/upcoming-context", { credentials: "same-origin" })
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          if (!cancelled) setData(d);
        })
        .catch(() => {});
    load();
    const id = window.setInterval(load, 5 * 60_000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  if (!data || !data.ok || !data.next) return null;
  const ev = data.next;
  const when = (() => {
    const t = new Date(ev.start_at).getTime();
    if (!Number.isFinite(t)) return "";
    const mins = Math.max(0, Math.round((t - Date.now()) / 60000));
    return mins <= 1 ? "now" : `in ${mins} min`;
  })();

  return (
    <section
      aria-label="Coming up"
      className="glass rounded-2xl border border-indigo-200/70 dark:border-indigo-800/40 backdrop-blur bg-gradient-to-br from-indigo-50/60 via-white/30 to-sky-50/40 dark:from-indigo-900/15 dark:via-slate-800/20 dark:to-sky-900/10 p-5 sm:p-6 space-y-2.5"
    >
      <div className="flex items-center gap-2 text-sm font-semibold text-indigo-700 dark:text-indigo-300">
        <CalendarClock className="w-4 h-4" />
        Coming up
        <span className="text-[11px] font-medium text-indigo-500/80 dark:text-indigo-400/80">
          {when}
        </span>
      </div>
      <div className="text-base font-bold text-slate-900 dark:text-slate-100">
        {ev.summary}
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
        {ev.location && (
          <span className="inline-flex items-center gap-1">
            <MapPin className="w-3.5 h-3.5" />
            {ev.location}
          </span>
        )}
        {ev.attendees.length > 0 && (
          <span className="inline-flex items-center gap-1">
            <Users className="w-3.5 h-3.5" />
            {ev.attendees.slice(0, 4).join(", ")}
            {ev.attendees.length > 4 ? ` +${ev.attendees.length - 4}` : ""}
          </span>
        )}
      </div>
      {ev.brief && (
        <p className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed">
          {ev.brief}
        </p>
      )}
      {data.more > 0 && (
        <p className="text-[11px] text-slate-400">
          +{data.more} more in the next hour
        </p>
      )}
    </section>
  );
}
